import { lazy, Suspense, useState, type FormEvent, type ReactNode } from 'react'
import { Keyboard } from '@phosphor-icons/react'
import { useI18n } from '@/i18n'
import { scanFailure, scanSuccess } from '@/lib/feedback'
import { Sheet } from '../Sheet'
import { Button } from '../Button'
import { Input } from '../Input'
import { Banner } from '../Banner'
import { Skeleton } from '../Skeleton'

const CameraScanner = lazy(() => import('./CameraScanner'))

/** Return an error message to refuse a code (wrong kind, wrong roll, already bound), or null. */
export type ScanValidator = (code: string) => string | null | Promise<string | null>

/** How the code arrived: read by the camera or typed by a person. */
export interface ScanMeta {
  entry: 'SCAN' | 'TYPED'
}

export interface ScannerSheetProps {
  open: boolean
  onClose: () => void
  /** `entry` says whether the camera read it or a person typed it (TYPED entries are counted, F4.6). */
  onScan: (code: string, meta: ScanMeta) => void
  validate?: ScanValidator
  title?: ReactNode
  /** Keep the camera open after each accepted code (put-away, counting a bay). */
  continuous?: boolean
  /** Normalise codes before validation (default: trim + upper-case). */
  normalize?: (code: string) => string
}

const defaultNormalize = (c: string) => c.trim().toUpperCase()

/**
 * Camera scanner with beep/vibrate feedback and a typed-code fallback (torn label, dead camera,
 * permission refused). Every code — scanned or typed — goes through the same `validate`.
 */
export function ScannerSheet({
  open,
  onClose,
  onScan,
  validate,
  title,
  continuous,
  normalize = defaultNormalize,
}: ScannerSheetProps) {
  const { t } = useI18n()
  const [manual, setManual] = useState(false)
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [lastAccepted, setLastAccepted] = useState<string | null>(null)
  const [cameraIssue, setCameraIssue] = useState<'no_camera' | 'permission' | null>(null)

  const close = () => {
    setManual(false)
    setTyped('')
    setError(null)
    setLastAccepted(null)
    setCameraIssue(null)
    onClose()
  }

  const accept = async (raw: string, entry: 'SCAN' | 'TYPED'): Promise<boolean> => {
    const code = normalize(raw)
    const problem = validate ? await validate(code) : null
    if (problem) {
      setError(problem)
      scanFailure()
      return false
    }
    setError(null)
    scanSuccess()
    onScan(code, { entry })
    if (continuous) setLastAccepted(code)
    else close()
    return true
  }

  const submitTyped = (e: FormEvent) => {
    e.preventDefault()
    if (typed.trim()) void accept(typed, 'TYPED').then((ok) => ok && setTyped(''))
  }

  const showManual = manual || cameraIssue !== null

  return (
    <Sheet open={open} onClose={close} title={title ?? t('scan.title')}>
      <div className="flex flex-col gap-4">
        {error && <Banner tone="error">{error}</Banner>}
        {!error && lastAccepted && (
          <Banner tone="success">
            <span className="font-mono">{lastAccepted}</span>
          </Banner>
        )}
        {cameraIssue && (
          <Banner tone="warn">
            {cameraIssue === 'permission' ? t('scan.permission') : t('scan.noCamera')}
          </Banner>
        )}

        {!showManual && (
          <Suspense fallback={<Skeleton className="aspect-[3/4] w-full sm:aspect-video" />}>
            <CameraScanner
              onDecode={(code) => void accept(code, 'SCAN')}
              onUnavailable={setCameraIssue}
            />
          </Suspense>
        )}

        {showManual ? (
          <form onSubmit={submitTyped} className="flex flex-col gap-3">
            <label className="text-[14px] font-semibold" htmlFor="scan-manual">
              {t('scan.manual')}
            </label>
            <Input
              id="scan-manual"
              size="touch"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoCapitalize="characters"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              className="font-mono uppercase"
              data-autofocus
            />
            <p className="text-[13px] text-muted">{t('scan.manualHint')}</p>
            <Button type="submit" size="touch" fullWidth disabled={!typed.trim()}>
              {t('scan.use')}
            </Button>
          </form>
        ) : (
          <Button
            variant="ghost"
            size="touch"
            leadingIcon={<Keyboard aria-hidden="true" />}
            onClick={() => setManual(true)}
          >
            {t('scan.manual')}
          </Button>
        )}
      </div>
    </Sheet>
  )
}
